import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, FormControl, InputLabel,
  Select, MenuItem, Chip, Grid, Autocomplete, Alert
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';

const TaskForm = ({ task, employees, projects, onSave, onCancel }) => {
  const { translate } = useLanguage();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    assignee: '',
    dueDate: '',
    estimatedHours: 0,
    budget: 0,
    project: '',
    tags: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        status: task.status || 'pending',
        priority: task.priority || 'medium',
        assignee: task.assignee || '',
        dueDate: task.dueDate ? task.dueDate.split('T')[0] : '',
        estimatedHours: task.estimatedHours || 0,
        budget: task.budget || 0,
        project: task.project || '',
        tags: task.tags || []
      });
    }
  }, [task]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    if (!formData.assignee) {
      newErrors.assignee = 'Assignee is required';
    }
    
    if (!formData.dueDate) {
      newErrors.dueDate = 'Due date is required';
    }
    
    if (formData.estimatedHours < 0) {
      newErrors.estimatedHours = 'Estimated hours must be positive';
    }
    
    if (formData.budget < 0) {
      newErrors.budget = 'Budget must be positive';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSave(formData);
    }
  };

  const handleTagChange = (event, newValue) => {
    setFormData(prev => ({ ...prev, tags: newValue }));
  };

  const commonTags = [
    'frontend', 'backend', 'database', 'api', 'ui/ux', 'testing',
    'deployment', 'security', 'performance', 'bugfix', 'feature',
    'documentation', 'maintenance', 'unifi', 'access-control'
  ];

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Task Title"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            error={!!errors.title}
            helperText={errors.title}
            required
          />
        </Grid>

        {/* Description */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            required
          />
        </Grid>

        {/* Status and Priority */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              label="Status"
            >
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="in_progress">In Progress</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Priority</InputLabel>
            <Select
              value={formData.priority}
              onChange={(e) => handleChange('priority', e.target.value)}
              label="Priority"
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Assignee and Due Date */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth error={!!errors.assignee}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={formData.assignee}
              onChange={(e) => handleChange('assignee', e.target.value)}
              label="Assignee"
              required
            >
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.name}>
                  {emp.name} ({emp.role})
                </MenuItem>
              ))}
            </Select>
            {errors.assignee && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                {errors.assignee}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Due Date"
            type="date"
            value={formData.dueDate}
            onChange={(e) => handleChange('dueDate', e.target.value)}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Project */}
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Project</InputLabel>
            <Select
              value={formData.project}
              onChange={(e) => handleChange('project', e.target.value)}
              label="Project"
            >
              {projects.map(project => (
                <MenuItem key={project.id} value={project.name}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* Estimated Hours */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Estimated Hours"
            type="number"
            value={formData.estimatedHours}
            onChange={(e) => handleChange('estimatedHours', parseFloat(e.target.value) || 0)}
            error={!!errors.estimatedHours}
            helperText={errors.estimatedHours}
            inputProps={{ min: 0, step: 0.5 }}
          />
        </Grid>

        {/* Budget */}
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Budget ($)"
            type="number"
            value={formData.budget}
            onChange={(e) => handleChange('budget', parseFloat(e.target.value) || 0)}
            error={!!errors.budget}
            helperText={errors.budget}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Grid>

        {/* Tags */}
        <Grid item xs={12}>
          <Autocomplete
            multiple
            freeSolo
            options={commonTags}
            value={formData.tags}
            onChange={handleTagChange}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  size="small"
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Add tags..."
                helperText="Press Enter to add custom tags"
              />
            )}
          />
        </Grid>

        {/* Error Display */}
        {Object.keys(errors).length > 0 && (
          <Grid item xs={12}>
            <Alert severity="error">
              Please fix the errors above before saving.
            </Alert>
          </Grid>
        )}

        {/* Actions */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TaskForm; 